import React, { useState, useEffect } from 'react';
// Tambahkan getDoc, collection, query, where, getDocs
import { doc, onSnapshot, updateDoc, arrayUnion, arrayRemove, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig'; // Sesuaikan path jika perlu

export default function BlockedMitraManager({ userId }) {
  const [blockedMitras, setBlockedMitras] = useState([]);
  const [newMitraId, setNewMitraId] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // 1. Tarik data blacklist secara Real-Time beserta Namanya
  useEffect(() => {
    if (!userId) return;

    // Fungsi asinkron untuk mencari nama mitra di berbagai koleksi
    const getMitraDetails = async (rawMitraId) => {
      if (!rawMitraId) return { id: 'N/A', name: 'ID Kosong', role: 'unknown' };
      
      // 🔥 BERSIHKAN ID DARI SPASI
      let mitraId = String(rawMitraId).trim();

      // 🔥 AUTO-KOREKSI HURUF KECIL: Jika formatnya pendek dan ada strip (misal zt-123 -> ZT-123)
      if (mitraId.length < 15 && mitraId.includes('-')) {
        const parts = mitraId.split('-');
        mitraId = parts[0].toUpperCase() + '-' + parts.slice(1).join('-');
      }

      const collections = ['therapists', 'cleaners', 'drivers'];
      
      // Tahap 1: Cari berdasarkan Document ID (UID)
      for (const colName of collections) {
        try {
          const docRef = doc(db, colName, mitraId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            return { id: mitraId, name: docSnap.data().name || 'Tanpa Nama', role: colName };
          }
        } catch (e) {
          // Tampilkan log merah di inspect element jika akses ditolak Firestore
          console.warn(`Cek Document ID gagal di koleksi ${colName}:`, e); 
        }
      }

      // Tahap 2: Jika tidak ketemu, cari berdasarkan 'id_number' (Format: ZT-xxx / ZC-xxx)
      for (const colName of collections) {
        try {
          const q = query(collection(db, colName), where('id_number', '==', mitraId));
          const querySnap = await getDocs(q);
          if (!querySnap.empty) {
            return { id: mitraId, name: querySnap.docs[0].data().name || 'Tanpa Nama', role: colName };
          }
        } catch (e) {
          console.warn(`Cek Query id_number gagal di koleksi ${colName}:`, e);
        }
      }

      // Tahap 3: Jika akun mitra sudah dihapus dari database
      return { id: mitraId, name: 'Nama Tidak Ditemukan', role: 'unknown' };
    };

    const userRef = doc(db, 'users', userId);
    const unsub = onSnapshot(userRef, async (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const legacyBlocked = data.blockedTherapists || [];
        const universalBlocked = data.blocked_mitras || [];
        const combinedIds = [...new Set([...legacyBlocked, ...universalBlocked])];
        
        // Looping untuk mencari detail setiap ID secara paralel
        const detailedMitras = await Promise.all(
          combinedIds.map(async (id) => await getMitraDetails(id))
        );
        
        setBlockedMitras(detailedMitras);
      }
    });

    return () => unsub();
  }, [userId]);

  // Map untuk translate nama koleksi ke bahasa Indonesia yang enak dibaca
  const roleNames = {
    therapists: 'Terapis',
    cleaners: 'Cleaner',
    drivers: 'Driver',
    unknown: 'Tidak Diketahui'
  };

  // 2. Fungsi Admin Memblokir Mitra Secara Manual
  const handleAddBlock = async () => {
    if (!newMitraId.trim()) return;
    setIsProcessing(true);
    
    try {
      let idToBlock = newMitraId.trim();

      // 🔥 AUTO-KOREKSI INPUT ADMIN: Ubah zt-12345 jadi ZT-12345 sebelum masuk database
      if (idToBlock.length < 15 && idToBlock.includes('-')) {
        const parts = idToBlock.split('-');
        idToBlock = parts[0].toUpperCase() + '-' + parts.slice(1).join('-');
      }

      const userRef = doc(db, 'users', userId);
      // arrayUnion akan memastikan tidak ada ID ganda yang masuk ke dalam array
      await updateDoc(userRef, {
        blocked_mitras: arrayUnion(idToBlock)
      });
      
      setNewMitraId(''); // Kosongkan input setelah sukses
      alert('Mitra berhasil diblokir untuk pengguna ini.');
    } catch (error) {
      console.error("Gagal menambah blokir:", error);
      alert('Terjadi kesalahan saat memblokir mitra.');
    } finally {
      setIsProcessing(false);
    }
  };

  // 3. Fungsi Admin Mencabut Blokir
  const handleUnblock = async (mitraId) => {
    if (!window.confirm(`Yakin ingin mencabut blokir untuk mitra ID: ${mitraId}?`)) return;
    setIsProcessing(true);

    try {
      const userRef = doc(db, 'users', userId);
      // arrayRemove akan menghapus spesifik ID tersebut dari array
      // Kita jalankan di kedua field untuk memastikan benar-benar bersih
      await updateDoc(userRef, {
        blocked_mitras: arrayRemove(mitraId),
        blockedTherapists: arrayRemove(mitraId) 
      });
    } catch (error) {
      console.error("Gagal mencabut blokir:", error);
      alert('Terjadi kesalahan saat mencabut blokir.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 shadow-lg mt-6">
      <div className="flex items-center gap-2 mb-4 border-b border-gray-800 pb-3">
        <IconBlock /> {/* Ganti dengan Icon dari library Anda jika ada */}
        <h3 className="text-lg font-semibold text-white">Daftar Mitra Diblokir (Blacklist)</h3>
      </div>
      
      <p className="text-sm text-gray-400 mb-4">
        Mitra yang ada di daftar ini tidak akan pernah mendapatkan orderan dari pelanggan ini lagi.
      </p>

      {/* TAMPILAN DAFTAR BLOKIR */}
      {blockedMitras.length === 0 ? (
        <div className="p-4 bg-gray-800/50 rounded-lg text-center text-gray-500 text-sm mb-6">
          Tidak ada mitra yang diblokir oleh pengguna ini.
        </div>
      ) : (
        <div className="space-y-2 mb-6">
          {blockedMitras.map((mitra) => (
            <div key={mitra.id} className="flex justify-between items-center bg-gray-800 p-3 rounded-lg border border-gray-700">
              <div>
                <p className="text-white font-semibold text-sm">
                  {mitra.name} <span className="text-xs text-gray-400 font-normal">({roleNames[mitra.role] || mitra.role})</span>
                </p>
                <p className="text-gray-500 font-mono text-xs mt-0.5">{mitra.id}</p>
              </div>
              <button 
                onClick={() => handleUnblock(mitra.id)}
                disabled={isProcessing}
                className="text-xs bg-red-900/40 hover:bg-red-600 text-red-400 hover:text-white px-3 py-1.5 rounded transition border border-red-500/30"
              >
                Cabut Blokir
              </button>
            </div>
          ))}
        </div>
      )}

      {/* FORM ADMIN TAMBAH BLOKIR MANUAL */}
      <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
        <p className="text-sm text-gray-300 font-semibold mb-2">Tambahkan Blokir Manual</p>
        <div className="flex gap-2">
          <input 
            type="text" 
            value={newMitraId}
            onChange={(e) => setNewMitraId(e.target.value)}
            placeholder="Masukkan ID Mitra (Contoh: ZT-00012 atau UID)"
            className="flex-1 bg-gray-900 border border-gray-700 rounded-lg p-2 text-white text-sm focus:border-orange-500 focus:outline-none"
          />
          <button 
            onClick={handleAddBlock}
            disabled={isProcessing || !newMitraId.trim()}
            className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition disabled:opacity-50"
          >
            Blokir
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper untuk icon sederhana
const IconBlock = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
  </svg>
);